import { Grid, Paper } from '@material-ui/core';
import moment from 'moment';
import React, { useEffect, useState } from 'react';

import { getInventoryRouteByID, IInventoryRoute } from '~/src/dash/api/inventory_routes';
import { IDataItem, MakeDashes, MakeDot } from '~/src/dash/components/dashboard/dashboard-graph';
import { ISelectedMetrics } from '~/src/dash/components/live-data/live-data';
import { LiveDataResourceType } from '~/src/dash/components/live-data/live-data';
import { parseLocation } from '~/src/dash/router/routes';
import { IHistoryProps } from '~/src/dash/types/props';
import { graphColors } from '~/src/dash/utils/colors';
import { LoadingStateHasData } from '~/src/dash/utils/loading-states';

interface ICustomTooltipPayloadItem {
  color: string;
  dataKey:
    | 'Date'
    | 'CDate'
    | 'DateLabel'
    | 'Revenue'
    | 'Impressions'
    | 'CPM'
    | 'EndpointRequests'
    | 'FillRate'
    | 'RenderRate'
    | 'CRevenue'
    | 'CImpressions'
    | 'CCPM'
    | 'CEndpointRequests'
    | 'CFillRate'
    | 'CRenderRate'
    | 'CAvgImpECPM'
    | 'AvgImpECPM'
    | 'EventAuctionInits'
    | 'WinningBids'
    | 'CWinningBids'
    | 'EndpointLatency'
    | 'AuctionLatency'
    | 'BidderLatency'
    | 'BidRequests'
    | 'BidError'
    | 'BidTimeout'
    | 'BidResponses'
    | 'CPMPerSecond'
    | 'CCPMPerSecond'
    | 'BidResponseRate'
    | 'CBidResponseRate'
    | 'BidTimeoutsRate'
    | 'CBidTimeoutsRate'
    // stream
    | 'StreamAdChunklistResponse'
    | 'StreamStart'
    | 'StreamPodRequest'
    | 'StreamAdFilled'
    | 'StreamAdImpression'
    | 'StreamAdComplete'
    // campaign
    | 'AdserverRequest'
    | 'AdserverFilled'
    | 'AdserverImpression'
    | 'AdserverComplete'
    | 'AdserverInventoryRequest'
    | 'AdserverIdealImpressionCount'
    | 'AdserverPacedRequestTest'
    | 'AdserverImpressionTest'

    // automated bid floor
    | 'BidFloorOptimized'
    | 'BidFloorLow'
    | 'BidFloorHigh'
    | 'BidRequestsOptimized'
    | 'BidRequestsLow'
    | 'BidRequestsHigh'
    | 'CPMOptimized'
    | 'CPMLow'
    | 'CPMHigh'
    | 'BidResponsesOptimized'
    | 'BidResponsesLow'
    | 'BidResponsesHigh'
    | 'WinningBidsOptimized'
    | 'WinningBidsLow'
    | 'WinningBidsHigh'
    | 'EstimatedRevenueOptimized'
    | 'EstimatedRevenueLow'
    | 'EstimatedRevenueHigh';
  value: any;
  payload: IDataItem;
}

interface IProps extends IHistoryProps {
  active?: boolean;
  payload?: ICustomTooltipPayloadItem[];
  selectedMetrics: ISelectedMetrics;
  selectedIDs: string[];
  resourceType: LiveDataResourceType;
  publisherID: string;
  loaded: number | boolean;
  width?: number;
  currencySymbol: string;
  needLegend: boolean;
}

interface ICustomTooltipDataPoint {
  dataKey: string;
  dateString: string | moment.Moment | undefined;
  color: string;
  valueString: string;
  dashed: boolean;
}

// 23.355 => "23"
const convertValueToSimpleInt = (value: number): string => {
  value = value || 0;
  return value.toLocaleString('en', { minimumFractionDigits: 0 });
};

// 23.355 => "23.36%"
const convertValueToPercentage = (value: number): string => {
  value = value || 0;
  return value.toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';
};

// 23.355 => "23"
const convertValueToIntNoDecimal = (value: number): string => {
  value = value || 0;
  return value.toLocaleString('en', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

// 23.355 => "$23.36"
const convertValueToCashWithDecimal = (value: number, currencySymbol: string): string => {
  value = value || 0;
  return (
    currencySymbol +
    value.toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  );
};

export const LiveDataGraphTooltip = (props: IProps) => {
  const { active, payload, selectedMetrics, selectedIDs, loaded, currencySymbol } = props;
  const [namesMap, setNamesMap] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (props.needLegend) {
      if (props.resourceType === 'InventoryRoute') {
        const loc = parseLocation(props.location.pathname).inventoryrouteid || '';
        if (loc !== '') {
          getInventoryRouteByID(props.publisherID, loc).then((currentRoute: IInventoryRoute) => {
            setNamesMap(
              currentRoute.Destinations.reduce(
                (destinations, newDestination) => ({
                  ...destinations,
                  [newDestination.ID]: newDestination.Name,
                }),
                {},
              ),
            );
          });
        }
      }
    }
  }, []);

  if (active && loaded === LoadingStateHasData) {
    // build an object for grouping any dataPoints that share the same key in a stacked bar
    const dataPoints: { [key: string]: ICustomTooltipDataPoint[] } = {};
    // ensure the legend, if needed, is the first item in dataPoints for rendering
    if (props.needLegend) {
      dataPoints.legend = [];
    }
    const legend: { [key: string]: ICustomTooltipDataPoint } = {};
    (payload || []).forEach((datum: ICustomTooltipPayloadItem) => {
      const { color, dataKey, value, payload } = datum;
      // for each item, create a date, data and value string to display on the tool tip
      let valueString: string = '';
      let dataKeyString: string = dataKey;
      let bidFloorLowValueString: string = '';
      let bidFloorHighValueString: string = '';
      const dateString = payload.DateTooltip;

      // check for dataKey without id in selectedMetrics
      const dashed = !(dataKey.replace(/\d/g, '') in selectedMetrics) && dataKey !== 'CRevenue';

      if (props.needLegend) {
        const label = dataKey.replace(/\D/g, '');
        if (selectedIDs.includes(label)) {
          legend[label] = { dataKey, dateString: '', color, valueString: namesMap[label], dashed };
        }
      }
      const definedValue = value || 0;
      if (dataKey === 'Revenue') {
        valueString = convertValueToCashWithDecimal(definedValue, currencySymbol);
      } else if (dataKey === 'FillRate' || dataKey === 'CFillRate') {
        valueString = convertValueToPercentage(definedValue);
        dataKeyString = 'Fill Rate';
      } else if (dataKey === 'RenderRate' || dataKey === 'CRenderRate') {
        valueString = convertValueToPercentage(definedValue);
        dataKeyString = 'Render Rate';
      } else if (dataKey === 'BidResponseRate' || dataKey === 'CBidResponseRate') {
        valueString = convertValueToPercentage(definedValue);
        dataKeyString = 'Bid Response Rate';
      } else if (dataKey === 'BidTimeoutsRate' || dataKey === 'CBidTimeoutsRate') {
        valueString = convertValueToPercentage(definedValue);
        dataKeyString = 'Bid Timeout Rate';
      } else if (dataKey === 'EndpointRequests' || dataKey === 'CEndpointRequests') {
        valueString = convertValueToSimpleInt(definedValue);
        dataKeyString = 'Endpt Reqs';
      } else if (dataKey === 'CAvgImpECPM' || dataKey === 'AvgImpECPM' || dataKey === 'CPM') {
        valueString = convertValueToCashWithDecimal(definedValue, currencySymbol);
        dataKeyString = 'CPM';
      } else if (dataKey === 'CRevenue') {
        valueString = convertValueToCashWithDecimal(definedValue, currencySymbol);
        dataKeyString = 'Revenue';
      } else if (dataKey === 'Impressions' || dataKey === 'CImpressions') {
        valueString = convertValueToSimpleInt(definedValue);
        dataKeyString = 'Impressions';
      } else if (dataKey === 'EventAuctionInits') {
        valueString = convertValueToSimpleInt(definedValue);
        dataKeyString = 'Auction Inits';
      } else if (dataKey === 'WinningBids') {
        valueString = convertValueToSimpleInt(definedValue);
        dataKeyString = 'Winning Bids';
      } else if (dataKey === 'EndpointLatency') {
        valueString = convertValueToIntNoDecimal(definedValue);
        dataKeyString = 'Endpoint Latency';
      } else if (dataKey === 'AuctionLatency') {
        valueString = convertValueToIntNoDecimal(definedValue);
        dataKeyString = 'Auction Latency';
      } else if (dataKey === 'BidderLatency') {
        valueString = convertValueToIntNoDecimal(definedValue);
        dataKeyString = 'Bidder Latency';
      } else if (dataKey === 'BidError') {
        valueString = convertValueToSimpleInt(definedValue);
        dataKeyString = 'Bid Errors';
      } else if (dataKey === 'BidTimeout') {
        valueString = convertValueToSimpleInt(definedValue);
        dataKeyString = 'Bid Timeouts';
      } else if (dataKey === 'BidResponses') {
        valueString = convertValueToSimpleInt(definedValue);
        dataKeyString = 'Bid Responses';
      } else if (dataKey === 'BidRequests') {
        valueString = convertValueToSimpleInt(definedValue);
        dataKeyString = 'Bid Requests';
        // stream
      } else if (dataKey === 'StreamAdChunklistResponse') {
        valueString = convertValueToSimpleInt(definedValue);
        dataKeyString = 'Chunklist Response';
      } else if (dataKey === 'StreamStart') {
        valueString = convertValueToSimpleInt(definedValue);
        dataKeyString = 'Stream Starts';
      } else if (dataKey === 'StreamPodRequest') {
        valueString = convertValueToSimpleInt(definedValue);
        dataKeyString = 'Pod Requests';
      } else if (dataKey === 'StreamAdFilled') {
        valueString = convertValueToSimpleInt(definedValue);
        dataKeyString = 'Ads Filled';
      } else if (dataKey === 'StreamAdImpression') {
        valueString = convertValueToSimpleInt(definedValue);
        dataKeyString = 'Ad Impressions';
      } else if (dataKey === 'StreamAdComplete') {
        valueString = convertValueToSimpleInt(definedValue);
        dataKeyString = 'Ad Completes';
        // campaign
      } else if (dataKey === 'AdserverRequest') {
        valueString = convertValueToSimpleInt(definedValue);
        dataKeyString = 'Paced Requests';
      } else if (dataKey === 'AdserverFilled') {
        valueString = convertValueToSimpleInt(definedValue);
        dataKeyString = 'Filled';
      } else if (dataKey === 'AdserverImpression') {
        valueString = convertValueToSimpleInt(definedValue);
        dataKeyString = 'Impressions';
      } else if (dataKey === 'AdserverComplete') {
        valueString = convertValueToSimpleInt(definedValue);
        dataKeyString = 'Completes';
      } else if (dataKey === 'AdserverInventoryRequest') {
        valueString = convertValueToSimpleInt(definedValue);
        dataKeyString = 'Raw Requests';
      } else if (dataKey === 'AdserverIdealImpressionCount') {
        valueString = convertValueToSimpleInt(definedValue);
        dataKeyString = 'Ideal Even Impressions';
      } else if (dataKey === 'AdserverPacedRequestTest') {
        valueString = convertValueToSimpleInt(definedValue);
        dataKeyString = 'Paced Request Waterfall Test';
      } else if (dataKey === 'AdserverImpressionTest') {
        valueString = convertValueToSimpleInt(definedValue);
        dataKeyString = 'Impressions Waterfall Test';
        // inventory routing
        // check includes for stacked bar metrics as the keys will also contain the id of the corresponding item
      } else if (dataKey.includes('DestinationRequests')) {
        valueString = convertValueToSimpleInt(definedValue);
        dataKeyString = 'Destination Requests';
      } else if (dataKey.includes('DestinationWins')) {
        valueString = convertValueToSimpleInt(definedValue);
        dataKeyString = 'Destination Wins';
      } else if (dataKey.includes('DestinationResponses')) {
        valueString = convertValueToSimpleInt(definedValue);
        dataKeyString = 'Destination Responses';
      } else if (dataKey.includes('DestinationErrors')) {
        valueString = convertValueToSimpleInt(definedValue);
        dataKeyString = 'Destination Errors';
      } else if (dataKey.includes('DestinationNoFills')) {
        valueString = convertValueToSimpleInt(definedValue);
        dataKeyString = 'Destination No Fills';
      } else if (dataKey.includes('DestinationTimeouts')) {
        valueString = convertValueToSimpleInt(definedValue);
        dataKeyString = 'Destination Timeouts';
      } else if (dataKey.includes('DestinationLatency')) {
        valueString = convertValueToSimpleInt(definedValue);
        dataKeyString = 'Destination Latency (ms)';
        // automated bid floor
      } else if (dataKey === 'BidFloorOptimized') {
        valueString = convertValueToCashWithDecimal(definedValue, currencySymbol);
        dataKeyString = 'Bid Floor Optimized';
        bidFloorLowValueString = convertValueToCashWithDecimal(payload.BidFloorLow, currencySymbol);
        bidFloorHighValueString = convertValueToCashWithDecimal(
          payload.BidFloorHigh,
          currencySymbol,
        );
        dataPoints['Bid Floor Low'] = [
          {
            dataKey,
            dateString,
            color: graphColors.lightOrange,
            valueString: bidFloorLowValueString,
            dashed: false,
          },
        ];
        dataPoints['Bid Floor Optimized'] = [
          { dataKey, dateString, color: graphColors.lightOrange, valueString, dashed: false },
        ];
        dataPoints['Bid Floor High'] = [
          {
            dataKey,
            dateString,
            color: graphColors.lightOrange,
            valueString: bidFloorHighValueString,
            dashed: false,
          },
        ];
      } else if (dataKey === 'BidRequestsOptimized') {
        valueString = convertValueToSimpleInt(definedValue);
        dataKeyString = 'Bid Requests Optimized';
      } else if (dataKey === 'BidRequestsLow') {
        valueString = convertValueToSimpleInt(definedValue);
        dataKeyString = 'Bid Requests Low';
      } else if (dataKey === 'BidRequestsHigh') {
        valueString = convertValueToSimpleInt(definedValue);
        dataKeyString = 'Bid Requests High';
      } else if (dataKey === 'BidResponsesOptimized') {
        valueString = convertValueToSimpleInt(definedValue);
        dataKeyString = 'Bid Responses Optimized';
      } else if (dataKey === 'BidResponsesLow') {
        valueString = convertValueToSimpleInt(definedValue);
        dataKeyString = 'Bid Responses Low';
      } else if (dataKey === 'BidResponsesHigh') {
        valueString = convertValueToSimpleInt(definedValue);
        dataKeyString = 'Bid Responses High';
      } else if (dataKey === 'WinningBidsOptimized') {
        valueString = convertValueToSimpleInt(definedValue);
        dataKeyString = 'Winning Bids Optimized';
      } else if (dataKey === 'WinningBidsLow') {
        valueString = convertValueToSimpleInt(definedValue);
        dataKeyString = 'Winning Bids Low';
      } else if (dataKey === 'WinningBidsHigh') {
        valueString = convertValueToSimpleInt(definedValue);
        dataKeyString = 'Winning Bids High';
      } else if (dataKey === 'CPMOptimized') {
        valueString = convertValueToCashWithDecimal(definedValue, currencySymbol);
        dataKeyString = 'CPM Optimized';
      } else if (dataKey === 'CPMLow') {
        valueString = convertValueToCashWithDecimal(definedValue, currencySymbol);
        dataKeyString = 'CPM Low';
      } else if (dataKey === 'CPMHigh') {
        valueString = convertValueToCashWithDecimal(definedValue, currencySymbol);
        dataKeyString = 'CPM High';
      } else if (dataKey === 'EstimatedRevenueOptimized') {
        valueString = convertValueToCashWithDecimal(definedValue, currencySymbol);
        dataKeyString = 'Estimated Revenue Optimized';
      } else if (dataKey === 'EstimatedRevenueLow') {
        valueString = convertValueToCashWithDecimal(definedValue, currencySymbol);
        dataKeyString = 'Estimated Revenue Low';
      } else if (dataKey === 'EstimatedRevenueHigh') {
        valueString = convertValueToCashWithDecimal(definedValue, currencySymbol);
        dataKeyString = 'Estimated Revenue High';
      }

      if (!dataPoints[dataKeyString]) {
        dataPoints[dataKeyString] = [];
      }
      if (dataKeyString !== 'Bid Floor Optimized') {
        dataPoints[dataKeyString].push({ dataKey, dateString, color, valueString, dashed });
      }
    });
    dataPoints.legend = Object.values(legend);

    return (
      <Paper
        className='custom-tooltip'
        style={{
          backgroundColor: '#f3f3f3',
          padding: 10,
          width: props.width || 300,
        }}
      >
        {Object.entries(dataPoints).map(([dataKeyString, data]) => {
          return (
            <Grid>
              {dataKeyString !== 'legend' && (
                <Grid style={{ fontSize: 12, fontWeight: 500 }}>{dataKeyString}</Grid>
              )}
              {data.map((dataPoint) => {
                if (
                  !/\d/.test(dataPoint.dataKey) ||
                  selectedIDs.includes(dataPoint.dataKey.replace(/\D/g, ''))
                ) {
                  const justifyString = dataKeyString === 'legend' ? 'flex-start' : 'space-between';
                  return (
                    <Grid
                      container
                      alignItems='center'
                      justify={justifyString}
                      spacing={1}
                      key={dataPoint.dataKey}
                    >
                      {dataPoint.dashed ? MakeDashes(dataPoint.color) : MakeDot(dataPoint.color)}
                      <Grid
                        item
                        xs={4}
                        style={{
                          fontWeight: 500,
                          fontSize: 12,
                        }}
                      >
                        {dataPoint.valueString}
                      </Grid>
                      {dataKeyString !== 'legend' && (
                        <Grid
                          item
                          xs={6}
                          // alignItems='flex-end' // remove this line after testing -> can only be applied on container
                          alignContent='flex-end'
                          style={{
                            fontWeight: 500,
                            fontSize: 12,
                          }}
                        >
                          {String(dataPoint.dateString)}
                        </Grid>
                      )}
                    </Grid>
                  );
                } else {
                  return null;
                }
              })}
            </Grid>
          );
        })}
      </Paper>
    );
  } else {
    return null;
  }
};
